import { useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';

import useIsMounted from '@swf/core/hooks/useIsMounted';
import type { Dispatch } from '@swf/core/store';
import type { HgGetPlayersBasedOnStatus } from '@swf/generated/types/api/v0/SwHomeGames';
import { useGetPlayersBasedOnStatus } from '@swf/poker';

import MembersContext from '../components/Club/Members/MembersContext/MembersContext';

const useGetClubMembers = () => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { modal } = useDispatch<Dispatch>();
  const { setState } = useContext(MembersContext);
  const isMounted = useIsMounted();
  const {
    loading,
    response: { data, error, message },
    handleGetPlayers
  } = useGetPlayersBasedOnStatus({ clubId: Number(clubId) });

  useEffect(() => {
    setState((state) => ({ ...state, loading }));
  }, [loading, setState]);

  useEffect(() => {
    if (!isMounted()) return;

    if (error) {
      modal.enqueue({
        path: '/notifications/simple-notification',
        locationState: { msg: error }
      });
    } else if (data) {
      setState((prevState) => ({ ...prevState, members: data }));
    }
  }, [data, error, modal, setState, isMounted]);

  const handleRequest = useCallback(
    (statuses?: HgGetPlayersBasedOnStatus['statuses']) => {
      handleGetPlayers({
        avatarIncluded: false,
        clubId: Number(clubId),
        statuses
      });
    },
    [clubId, handleGetPlayers]
  );

  return {
    response: {
      data,
      error,
      message
    },
    handleRequest
  };
};

export default useGetClubMembers;
